import React, { useEffect, useMemo, useState } from 'react';
import { Clock3 } from 'lucide-react';
import { motion } from 'framer-motion';
import { getRegionMeta } from '../utils/region';
type LiveOrder = {
  material: string;
  quantity: number;
  city: string;
  createdAt: Date;
};
const ORDER_TEMPLATES = [{
  material: '5/8" Minus',
  quantity: 12,
  fallbackCity: 'Tacoma'
}, {
  material: '3-Way Topsoil',
  quantity: 15,
  fallbackCity: 'Kent'
}, {
  material: 'Drain Rock',
  quantity: 10,
  fallbackCity: 'Puyallup'
}, {
  material: 'Medium Bark',
  quantity: 14,
  fallbackCity: 'Bellevue'
}, {
  material: 'Pea Gravel',
  quantity: 8,
  fallbackCity: 'Auburn'
}, {
  material: '5/8" Clear',
  quantity: 11,
  fallbackCity: 'Renton'
}] as const;
type LiveOrderTickerProps = {
  isDispatchActive?: boolean;
  opensIn?: string;
};
export function LiveOrderTicker({
  isDispatchActive = true,
  opensIn = ''
}: LiveOrderTickerProps) {
  const [now, setNow] = useState(() => new Date());
  const orders = useMemo<LiveOrder[]>(() => {
    const cities = getRegionMeta().cities;
    const created = new Date();
    return ORDER_TEMPLATES.map((order, index) => {
      const createdAt = new Date(created);
      createdAt.setMinutes(createdAt.getMinutes() - (index * 19 + 6));
      return {
        material: order.material,
        quantity: order.quantity,
        city: cities[index % cities.length] || order.fallbackCity,
        createdAt
      };
    });
  }, []);
  useEffect(() => {
    // When dispatch closes, lock the relative time in place so this reads as
    // the last completed feed rather than implying orders are still arriving.
    setNow(new Date());
    if (!isDispatchActive) return;
    const timer = window.setInterval(() => setNow(new Date()), 60_000);
    return () => window.clearInterval(timer);
  }, [isDispatchActive]);
  const marqueeOrders = [...orders, ...orders];
  const lastOrder = orders[0];
  if (!isDispatchActive) {
    return <div className="flex min-w-0 flex-1 items-center overflow-hidden" aria-label={`Dispatch closed. ${opensIn}. Last delivery activity.`} data-id="element-1130" data-mp="2sq1i1oq3rj5p">
        <div className="mr-2 flex shrink-0 items-center gap-1.5" data-id="element-1131" data-mp="u6o8x8ahrynt">
          <Clock3 className="h-3 w-3 text-white/30" aria-hidden="true" data-id="element-1132" data-mp="u7g5omfts2fo" />
          <span className="text-[10px] font-bold uppercase tracking-[0.16em] text-white/60" data-id="element-1133" data-mp="pewj4zz5twyq">Dispatch closed</span>
          {opensIn && <span className="hidden border-l border-white/20 pl-1.5 text-[10px] font-medium tabular-nums text-white/45 sm:inline" data-id="element-1134" data-mp="kgl8wo4ydkfz">
              {opensIn}
            </span>}
        </div>
        <span className="mr-2 shrink-0 text-white/40" aria-hidden="true" data-id="element-1135" data-mp="belpjp6gu49v">•</span>
        <p className="min-w-0 truncate whitespace-nowrap text-[11px] font-medium text-white/45 sm:text-xs" title={`Last delivery activity: ${lastOrder.quantity} yd ${lastOrder.material} to ${lastOrder.city}`} data-id="element-1136" data-mp="6i526x73btxu">
          Last delivery: {lastOrder.quantity} yd {lastOrder.material}
          <span className="mx-1.5 text-white/35" data-id="element-1137" data-mp="1vvicd31vwc6v">→</span>
          {lastOrder.city}
        </p>
      </div>;
  }
  return <div className="flex min-w-0 flex-1 items-center overflow-hidden" aria-label="Recent orders" data-id="element-1138" data-mp="24bzeyiatclbv">
      <div className="mr-2 flex shrink-0 items-center gap-1.5" data-id="element-1139" data-mp="28dkmdo4kw7eo">
        <span className="text-[10px] font-bold uppercase tracking-[0.16em] text-[#00b774]" data-id="element-1140" data-mp="2d5cdq0vk6h5v">Live</span>
        <span className="flex h-3 items-end gap-0.5" aria-hidden="true" data-id="element-1141" data-mp="2i45uq3a20qas">
          {[0, 1, 2].map(index => <motion.span key={index} className="w-0.5 rounded-full bg-[#00b774]" animate={{
          height: ['35%', '100%', '35%']
        }} transition={{
          duration: 1.15,
          repeat: Infinity,
          delay: index * 0.18,
          ease: 'easeInOut'
        }} data-id="element-1142" data-mp="1le6edmnnwnhn" />)}
        </span>
      </div>
      <span className="mr-2 shrink-0 text-white/30" aria-hidden="true" data-id="element-1143" data-mp="1zdo1zyp81upf">•</span>
      <div className="min-w-0 overflow-hidden" data-id="element-1144" data-mp="1zbl833ky7hff">
        <motion.div className="flex w-max items-center whitespace-nowrap" animate={{
        x: ['0%', '-50%']
      }} transition={{
        duration: Math.max(52, orders.length * 9),
        ease: 'linear',
        repeat: Infinity
      }} data-id="element-1145" data-mp="35sylllbtk7yj">
          {marqueeOrders.map((order, index) => <span key={`${order.material}-${index}`} className="inline-flex items-center text-[11px] font-medium text-white/50 sm:text-xs" data-id="element-1146" data-mp="imc87e0zp92p" data-mpi={index}>
              <span className="text-white/50" data-id="element-1147" data-mp="wmn10mlv0xqi">{order.quantity} yd {order.material}</span>
              <span className="mx-1.5 text-[#00b774]" data-id="element-1148" data-mp="rkp9z677wyn0">→</span>
              <span data-id="element-1149" data-mp="16izov3uq4hdc">{order.city}</span>
              <span className="mx-1.5 text-white/35" data-id="element-1150" data-mp="16hyg5616k6ys">·</span>
              <span className="text-white/35" data-id="element-1151" data-mp="11ky6kl54gm2x">{formatRelativeTime(order.createdAt, now)}</span>
              <span className="mx-4 text-white/25" data-id="element-1152" data-mp="1fccqqvarmtbk">•</span>
            </span>)}
        </motion.div>
      </div>
    </div>;
}
function formatRelativeTime(createdAt: Date, now: Date): string {
  const minutes = Math.max(1, Math.round((now.getTime() - createdAt.getTime()) / 60_000));
  if (minutes < 60) return `${minutes} min ago`;
  return `${Math.round(minutes / 60)} hr ago`;
}